import { t } from "@travily/i18n";
import { Icon } from "@ui/components/Icon";
import { optionTint } from "@ui/components/internal/OptionTint";
import { ScrollArea } from "@ui/components/ScrollArea";
import { SelectableAvatar } from "@ui/components/SelectableAvatar";
import { StandardButton } from "@ui/components/StandardButton";
import { tv } from "@ui/lib/tv";
import { OverlayTriggerStateContext } from "@ui/primitives/Modal";
import type { Selection } from "@ui/primitives/shared";
import { ToggleButton } from "@ui/primitives/ToggleButton";
import { ToggleButtonGroup } from "@ui/primitives/ToggleButtonGroup";
import { useContext, useMemo } from "react";
import type { SearchMember, WhoSelection } from "../types";

const whoPopover = tv({
  slots: {
    root: "flex min-h-0 flex-col gap-4 p-5",
    title: "shrink-0 border-b border-neutral-100 pb-4 text-2xl/8 font-extrabold tracking-2xl text-neutral-900",
    panel: "flex min-h-0 flex-col",
    list: "flex flex-col gap-1",
    // Pinned below the scroller, so adding someone stays reachable however many
    // people the household has. A hairline keeps it out of the guests group.
    addSection: "mt-1 shrink-0 border-neutral-100 border-t pt-2",
    groupHeading: "px-2 pt-2 text-sm font-semibold uppercase tracking-[0.04em] text-neutral-500",
    text: "flex min-w-0 flex-1 flex-col",
    name: "truncate text-base font-semibold tracking-[-0.16px] text-neutral-900",
    meta: "truncate text-base/6 tracking-[-0.16px] text-neutral-600",
    addIcon: "flex size-11 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-700",
    upsell: "flex flex-col items-center gap-3 py-4 text-center",
    upsellIcon: "flex size-12 items-center justify-center rounded-full bg-lime-50 text-lime-700",
    upsellTitle: "text-lg font-bold tracking-[-0.18px] text-neutral-900",
    upsellBody: "text-base/6 text-neutral-600",
  },
});

// The row hover/selected tint follows the member's avatar colour (Figma "Drop / Who":
// each member's row highlights in its own pastel): the shared optionTint multi-select
// recipe, keyed by AvatarColor.
const memberRow = tv({
  base: "flex w-full cursor-pointer items-center gap-4 rounded-full py-1 pl-2 pr-3 text-left outline-none",
});

// Bleeds to the card edge so the rail sits where the design has it; the viewport
// pads the rows back in line with the heading. Matches Where and When.
const whoListClassName = "-mx-5";
const whoViewportClassName = "px-5";

export interface WhoPopoverProps {
  /** Whether the current account is a family account. Non-family accounts see the upsell. */
  isFamily: boolean;
  /** The family members available to pick (empty for non-family accounts). */
  members: SearchMember[];
  value: WhoSelection;
  onChange: (value: WhoSelection) => void;
  /** Whether the viewer already has a family account to switch to (vs. needing to create one). */
  hasFamilyAccount?: boolean;
  onCreateFamily?: () => void;
  onSwitchToFamily?: () => void;
  /** Opens the add-someone sheet. Omit to hide the add row. */
  onAddPerson?: () => void;
}

/**
 * "Alfie's friend | Age: 9", or just the age. No derived life-stage band: it can
 * only be computed from the age, so printing both says the same thing twice.
 * Only a typed membership title adds anything the age doesn't already carry.
 */
function memberMeta(member: SearchMember): string | null {
  const { age, title } = member;
  if (age == null) return title || null;
  const ageText = t("components.search_bar.who_member_age", { age });
  return title ? `${title} | ${ageText}` : ageText;
}

function MemberRow({ member }: { member: SearchMember }) {
  const styles = whoPopover();
  const meta = memberMeta(member);
  return (
    <ToggleButton
      className={({ isSelected }) =>
        memberRow({ className: optionTint({ color: member.color, isSelected, select: "multiple" }) })
      }
      id={String(member.id)}
    >
      {({ isSelected }) => (
        <>
          <SelectableAvatar
            avatar={member.avatar}
            badgeClassName="size-5"
            className="size-11"
            color={member.color}
            initial={member.initial}
            isSelected={isSelected}
          />
          <span className={styles.text()}>
            <span className={styles.name()}>{member.name || member.initial}</span>
            {meta && <span className={styles.meta()}>{meta}</span>}
          </span>
        </>
      )}
    </ToggleButton>
  );
}

/**
 * The Who body, shared by the desktop popover (below its title) and the mobile
 * stacked search (as an accordion section). Family accounts get a multi-select
 * list of members; everyone else gets an upsell to switch to / create a family
 * account. Multi-select, so — unlike the When panels — it never dismisses the
 * popover on a selection.
 */
export function WhoPanel({
  isFamily,
  members,
  value,
  onChange,
  hasFamilyAccount,
  onCreateFamily,
  onSwitchToFamily,
  onAddPerson,
}: WhoPopoverProps) {
  const styles = whoPopover();
  // Null in the mobile overlay, which renders this panel outside a popover.
  const popoverState = useContext(OverlayTriggerStateContext);
  const selectedKeys = useMemo<Selection>(() => new Set(value.memberIds.map(String)), [value.memberIds]);
  const household = useMemo(() => members.filter((member) => !member.guest), [members]);
  const guests = useMemo(() => members.filter((member) => member.guest), [members]);

  if (!isFamily) {
    return (
      <div className={styles.upsell()}>
        <span aria-hidden="true" className={styles.upsellIcon()}>
          <Icon name="members" type="sticker" />
        </span>
        <h3 className={styles.upsellTitle()}>{t("components.search_bar.who_upsell_title")}</h3>
        <p className={styles.upsellBody()}>
          {hasFamilyAccount
            ? t("components.search_bar.who_upsell_switch_body")
            : t("components.search_bar.who_upsell_create_body")}
        </p>
        <StandardButton
          color="pink"
          hierarchy="primary"
          onPress={hasFamilyAccount ? onSwitchToFamily : onCreateFamily}
          size="44"
        >
          {hasFamilyAccount ? t("components.search_bar.who_switch_cta") : t("components.search_bar.who_create_cta")}
        </StandardButton>
      </div>
    );
  }

  const handleSelectionChange = (keys: Selection) => {
    const ids = keys === "all" ? members.map((member) => member.id) : Array.from(keys, (key) => Number(key));
    onChange({ memberIds: ids });
  };

  // Guests only earn their own heading once there is one — an unsplit list needs
  // no labels, and a lone "Family" heading over everyone is noise.
  const isGrouped = guests.length > 0;

  return (
    <div className={styles.panel()}>
      <ScrollArea className={whoListClassName} viewportClassName={whoViewportClassName}>
        <ToggleButtonGroup
          aria-label={t("components.search_bar.who_title")}
          className={styles.list()}
          onSelectionChange={handleSelectionChange}
          selectedKeys={selectedKeys}
          selectionMode="multiple"
        >
          {isGrouped && <span className={styles.groupHeading()}>{t("components.search_bar.who_group_family")}</span>}
          {household.map((member) => (
            <MemberRow key={member.id} member={member} />
          ))}
          {isGrouped && <span className={styles.groupHeading()}>{t("components.search_bar.who_group_guests")}</span>}
          {guests.map((member) => (
            <MemberRow key={member.id} member={member} />
          ))}
        </ToggleButtonGroup>
      </ScrollArea>
      {onAddPerson && (
        <div className={styles.addSection()}>
          <button
            className={memberRow({ className: "hover:bg-neutral-50" })}
            // Dismiss the enclosing popover first — leaving it open puts it on top
            // of the sheet, and the click that dismisses it also dismisses the sheet.
            onClick={() => {
              popoverState?.close();
              onAddPerson();
            }}
            type="button"
          >
            <span aria-hidden="true" className={styles.addIcon()}>
              <Icon name="plus" />
            </span>
            <span className={styles.text()}>
              <span className={styles.name()}>{t("components.search_bar.who_add_title")}</span>
              <span className={styles.meta()}>{t("components.search_bar.who_add_description")}</span>
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

/** Desktop Who popover: the title from the Figma "Drop / Who" plus the shared body. */
export function WhoPopover(props: WhoPopoverProps) {
  const styles = whoPopover();
  return (
    <div className={styles.root()}>
      <h2 className={styles.title()}>{t("components.search_bar.who_title")}</h2>
      <WhoPanel {...props} />
    </div>
  );
}
